import PropTypes from "prop-types";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { useQuery } from "@tanstack/react-query";
import StatCard from "../../components/RestaurantAdminComponents/StatCard";
import CustomerSpendBreakdownCard from "../../components/RestaurantAdminComponents/CustomerSpendBreakdownCard";
import RecentRedemptionsCard from "../../components/RestaurantAdminComponents/RecentRedemptionsCard";
import SettlementCard from "../../components/RestaurantAdminComponents/SettlementCard";
import CustomButton from "../../components/AuthenticationComponents/CustomButton";
import SecondaryButton from "../../components/Common/SecondaryButton";
import DataTable from "../../components/RestaurantAdminComponents/DataTable";
import arrowRightIcon from "../../assets/images/authentication/arrow-right.svg";
import "../../assets/scss/custom/components/_card-banner.scss";

// API
import { get } from "../../api/axios";
import endpoints from "../../api/endpoints";

/* ============ inline icons ============ */

const sp = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
const IconDollar = () => (<svg {...sp}><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>);
const IconTicket = () => (<svg {...sp}><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" /><path d="M13 5v2" /><path d="M13 17v2" /><path d="M13 11v2" /></svg>);
const IconUsers = () => (<svg {...sp}><circle cx="9" cy="8" r="3" /><path d="M2 20c0-3.3 3.1-6 7-6s7 2.7 7 6" /><circle cx="17" cy="9" r="2.4" /><path d="M17.5 14.2c2.6.4 4.5 2.6 4.5 5.3" /></svg>);
const IconChart = () => (<svg {...sp}><path d="M3 3v18h18" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" /></svg>);
const IconCalendar = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>);
const IconChevronDown = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>);

const pick = (obj, ...paths) => {
  for (const path of paths) {
    const value = path
      .split(".")
      .reduce((acc, key) => (acc === null || acc === undefined ? acc : acc[key]), obj);
    if (value !== undefined && value !== null) return value;
  }
  return null;
};

const extractPayload = (queryData) => {
  if (!queryData) return null;
  if (typeof queryData.data?.data !== "undefined") return queryData.data.data;
  return queryData.data || null;
};

const currency = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") return fallback;
  const num = Number(value);
  if (Number.isNaN(num)) return fallback;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const normalizeAmount = (value) => {
  if (value && typeof value === "object") {
    if (value.amount !== undefined && value.amount !== null) return value.amount;
    if (value.value !== undefined && value.value !== null) return value.value;
    if (value.cents !== undefined && value.cents !== null) return value.cents / 100;
    return null;
  }
  return value;
};

const formatDateShort = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const normalizeTopCustomerAmount = (customer) => {
  const cents = pick(customer, "total_spend_cents", "spend_cents", "amount_spent_cents");
  if (cents !== null && cents !== undefined && cents !== "") return Number(cents) / 100;
  return Number(normalizeAmount(pick(customer, "total_spend", "total_spend.amount", "spend", "amount_spent")) || 0);
};

const StatusBadge = ({ status }) => {
  const map = {
    paid: "stl-badge--green",
    completed: "stl-badge--green",
    settlement_completed: "stl-badge--green",
    active: "stl-badge--green",
    pending: "stl-badge--orange",
    upcoming: "stl-badge--blue",
    failed: "stl-badge--red",
    no_platform_fee: "stl-badge--grey",
  };
  const key = (status || "").toLowerCase();
  return <span className={`stl-badge ${map[key] || "stl-badge--grey"}`}>{status || "Unknown"}</span>;
};

const PERIOD_LABELS = {
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
  all_time: "All Time",
  custom: "Custom Range",
};

const getPeriodLabel = (period) => {
  const labels = {
    weekly: "Weekly",
    monthly: "Monthly",
    yearly: "Yearly",
    all_time: "All Time",
    custom: "Custom Range",
  };
  return labels[period] || "Weekly";
};

/* -------------------------------------------------------------------------
 * RestaurantDashboardPage — assembles the dashboard from independently
 * reusable widgets. No sidebar or header here; content-only.
 * ---------------------------------------------------------------------- */

const RestaurantDashboardPage = ({
  onViewRedemptions,
  onViewCustomers,
  onViewAllRedemptions,
  onViewAllCustomers,
}) => {
  document.title = "Dashboard | Omaha Food Lovers";
  const navigate = useNavigate();

  const [period, setPeriod] = useState("monthly");
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ preset: "custom", start: "", end: "" });
  const [settlementPage, setSettlementPage] = useState(1);
  const [settlementRowsCache, setSettlementRowsCache] = useState([]);
  const [settlementHasMore, setSettlementHasMore] = useState(true);

  // Get current user ID for cache invalidation
  const getCurrentUserId = () => {
    const authUser = localStorage.getItem('authUser');
    if (authUser) {
      try {
        const user = JSON.parse(authUser);
        return user.id || user.user_id || user.email;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const userId = getCurrentUserId();

  // React Query hooks for each API call with user-specific cache keys
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['restaurantDashboard', userId, period, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("period", period);
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      return get(`${endpoints.restaurantDashboard}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: redeemsData, isLoading: redeemsLoading } = useQuery({
    queryKey: ['restaurantDashboardRedeems', userId, period, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("period", period);
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      return get(`${endpoints.restaurantDashboardRedeems}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: settlementData, isLoading: settlementLoading } = useQuery({
    queryKey: ['restaurantDashboardCurrentSettlement', userId, period, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("period", period);
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      return get(`${endpoints.restaurantDashboardCurrentSettlement}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: spendBreakdownData, isLoading: spendBreakdownLoading } = useQuery({
    queryKey: ['restaurantDashboardCustomerSpendBreakdown', userId, period, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("period", period);
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      return get(`${endpoints.restaurantDashboardCustomerSpendBreakdown}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: topCustomersData, isLoading: topCustomersLoading } = useQuery({
    queryKey: ['restaurantDashboardTopCustomers', userId, period, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("period", period);
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      return get(`${endpoints.restaurantDashboardTopCustomers}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: recentRedemptionsData, isLoading: recentRedemptionsLoading } = useQuery({
    queryKey: ['restaurantDashboardRecentRedemptions', userId, period, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("period", period);
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      return get(`${endpoints.restaurantDashboardRecentRedemptions}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: settlementsData, isLoading: settlementsLoading, isFetching: settlementsFetching } = useQuery({
    queryKey: ['restaurantSettlementInvoicesDashboard', userId, settlementPage, period, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("page", settlementPage);
      params.append("per_page", 20);
      params.append("sort_by", "period_start");
      params.append("sort_direction", "desc");
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      return get(`${endpoints.restaurantSettlementInvoices}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: restaurantMeData, isLoading: restaurantMeLoading } = useQuery({
    queryKey: ['restaurantMe', userId],
    queryFn: () => get(endpoints.restaurantMe),
    staleTime: 5 * 60 * 1000,
  });

  const loading = dashboardLoading || redeemsLoading || settlementLoading || spendBreakdownLoading || topCustomersLoading || recentRedemptionsLoading;

  // Check if restaurant has an active connect account from restaurant me API
  const hasActiveConnectAccount = restaurantMeData?.data?.data?.current_restaurant?.account_is_active === true;

  // Navigation handlers
  const handleViewRedemptions = () => {
    navigate('/restaurant/redemptions');
  };

  const handleViewCustomers = () => {
    navigate('/restaurant/customers');
  };

  const handleViewAllRedemptions = () => {
    navigate('/restaurant/redemptions');
  };

  const handleViewAllCustomers = () => {
    navigate('/restaurant/customers');
  };

  const handleSetupConnectAccount = () => {
    navigate('/restaurant/wallet');
  };

  const handleLoadMoreSettlements = () => {
    if (!settlementsFetching && settlementHasMore) {
      setSettlementPage((prev) => prev + 1);
    }
  };

  // Helper functions
  const calculateDelta = (previous, current) => {
    if (!previous || previous === 0) return 0;
    const change = ((current - previous) / previous) * 100;
    return change;
  };

  const transformSettlementData = (data) => {
    if (!data) return null;

    const { period, settlement } = data;

    // Check if settlement exists
    if (!settlement) {
      return {
        periodLabel: period?.label || "No data",
        lineItems: [],
        net: { label: "Net Settlement", amount: 0 },
        note: "No settlement data available",
        chartSegments: [],
      };
    }

    // Map line items from settlement data using display values
    const lineItems = [
      {
        label: "Vouchers Redeemed",
        amount: settlement.vouchers_redeemed || 0,
        display: settlement.vouchers_redeemed_display || "$0.00",
      },
      {
        label: "Platform Fee",
        amount: settlement.tech_fee || 0,
        display: settlement.tech_fee_display || "$0.00",
      },
      {
        label: "Reward Funding",
        amount: settlement.reward_funding || 0,
        display: settlement.reward_funding_display || "$0.00",
      },
    ];

    const settlementAmountPayable = normalizeAmount(pick(settlement, "settlement_amount_payable.amount")) ?? 0;

    // Settlement amount payable
    const net = {
      label: "Settlement Amount Payable",
      amount: settlementAmountPayable,
      display: currency(settlementAmountPayable),
    };

    // Generate chart segments from line items
    const chartSegments = lineItems.map((item, index) => {
      const colors = ['#e23d3d', '#1ba97f', '#f59e0b'];
      return {
        label: item.label,
        value: item.amount,
        color: colors[index % colors.length],
      };
    });

    return {
      periodLabel: period?.subtitle || period?.label || "No data",
      lineItems,
      net,
      note: settlement.settlement_note || `Payout Date: ${settlement.payout_date_display || 'N/A'}`,
      chartSegments,
      totalMemberSpend: settlement.total_member_spend_display || "$0.00",
      estimatedNetRevenue: settlement.estimated_net_restaurant_revenue_display || "$0.00",
      status: settlement.display_status || settlement.status || "Unknown",
    };
  };

  const transformSpendBreakdownData = (data) => {
    if (!data) return null;

    const { segments, period } = data;

    // Find returning and new customer segments
    const returningSegment = segments?.find(seg => seg.key === 'returning');
    const newSegment = segments?.find(seg => seg.key === 'new');

    return {
      description: `Customer spend distribution for ${period?.label || 'current period'}`,
      returning: {
        percent: returningSegment?.percentage || 0,
        amount: returningSegment?.spend || 0,
      },
      newCustomers: {
        percent: newSegment?.percentage || 0,
        amount: newSegment?.spend || 0,
      },
    };
  };

  const transformTopCustomersData = (data) => {
    if (!data) return null;

    const customersPayload = data.customers && typeof data.customers === "object" && !Array.isArray(data.customers)
      ? data.customers.data
      : data.customers;
    const customers = Array.isArray(customersPayload)
      ? customersPayload
      : Array.isArray(data.data)
        ? data.data
        : [];
    const seen = new Set();

    // Map API customers to component format
    return customers.reduce((rows, customer, index) => {
      const id = customer.id ?? customer.user_id ?? customer.customer_id ?? index;
      if (seen.has(id)) return rows;
      seen.add(id);

      const name = customer.name || customer.full_name || `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "Unknown";
      const totalSpend = normalizeTopCustomerAmount(customer);

      rows.push({
        id,
        avatarUrl: customer.avatar_url || customer.profile_image || `https://i.pravatar.cc/150?u=${id}`,
        name,
        email: customer.email || "N/A",
        type: customer.customer_type_label || customer.customer_type || customer.status_label || customer.status || "Unknown",
        totalSpend,
        totalSpendDisplay: customer.total_spend_display || currency(totalSpend),
        totalVisits: customer.visits ?? customer.total_visits ?? customer.transaction_count ?? 0,
        pointsEarned: customer.points_earned ?? customer.total_points_earned ?? customer.points ?? 0,
        firstVisit: formatDateShort(customer.first_visit_at),
        lastVisit: formatDateShort(customer.last_visit_at || customer.latest_visit_at || customer.last_transaction_at),
      });

      return rows;
    }, []);
  };

  const transformRecentRedemptionsData = (data) => {
    if (!data) return null;

    const { redemptions } = data;

    // Map API redemptions to component format
    return redemptions?.map(redemption => ({
      id: redemption.id,
      code: redemption.code || 'N/A',
      name: redemption.customer?.name || `${redemption.customer?.first_name || ''} ${redemption.customer?.last_name || ''}`.trim() || 'Unknown',
      email: redemption.customer?.email || 'N/A',
      value: redemption.value || redemption.value_cents / 100 || 0,
      status: redemption.status_label || redemption.status || 'Unknown',
      date: redemption.redeemed_at_display || redemption.redeemed_at || 'Unknown',
    })) || [];
  };

  // Transform API data to component format
  const totalMemberSpend = dashboardData?.data?.data ? {
    value: (dashboardData.data.data.overview.total_spend || 0) / 100,
    deltaPct: calculateDelta(dashboardData.data.data.trend.this_week.total_spend, dashboardData.data.data.trend.today.total_spend),
    secondaryValue: dashboardData.data.data.trend.this_month.total_spend / 100 || 0,
  } : {
    value: loading ? 0 : 0,
    deltaPct: null,
    secondaryValue: 0,
  };

  const vouchersRedeemed = redeemsData?.data?.data ? {
    value: redeemsData.data.data.total_redeemed,
    deltaPct: calculateDelta(redeemsData.data.data.this_week, redeemsData.data.data.today),
    secondaryValue: redeemsData.data.data.this_month || 0,
  } : {
    value: loading ? 0 : 0,
    deltaPct: null,
    secondaryValue: 0,
  };

  const uniqueCustomers = dashboardData?.data?.data ? {
    value: dashboardData.data.data.overview.unique_members,
    deltaPct: calculateDelta(dashboardData.data.data.trend.this_week.unique_members, dashboardData.data.data.trend.today.unique_members),
    secondaryValue: dashboardData.data.data.trend.this_month.unique_members || 0,
  } : {
    value: loading ? 0 : 0,
    deltaPct: null,
    secondaryValue: 0,
  };

  const settlement = transformSettlementData(settlementData?.data?.data) || {
    periodLabel: loading ? "Loading..." : "No data",
    lineItems: loading ? [{ label: "Loading...", amount: 0 }] : [],
    net: { label: "Net Settlement", amount: 0 },
    note: loading ? "Loading settlement data..." : "No settlement data available",
    chartSegments: loading ? [{ label: "Loading...", value: 0, color: "#ccc" }] : [],
  };

  const spendBreakdown = transformSpendBreakdownData(spendBreakdownData?.data?.data) || {
    description: loading ? "Loading..." : "No data",
    returning: { percent: 0, amount: 0 },
    newCustomers: { percent: 0, amount: 0 },
  };

  const topCustomers = transformTopCustomersData(topCustomersData?.data?.data) || [];

  const topCustomerColumns = useMemo(() => [
    {
      key: "customer",
      label: "Customer",
      width: "1.6fr",
      render: (row) => (
        <span className="rp-table__row-label">
          <img src={row.avatarUrl} alt="" className="rp-avatar" />
          <div>
            <div>{row.name}</div>
            <div style={{ fontSize: "12px", color: "#888" }}>{row.email}</div>
          </div>
        </span>
      ),
    },
    { key: "type", label: "Type", width: "0.8fr", render: (row) => <StatusBadge status={row.type} /> },
    { key: "totalSpend", label: "Total Spend", width: "1fr", render: (row) => row.totalSpendDisplay },
    { key: "totalVisits", label: "Total Visits", width: "0.8fr", align: "right" },
    { key: "pointsEarned", label: "Points Earned", width: "1fr", align: "right", render: (row) => Number(row.pointsEarned || 0).toLocaleString() },
    { key: "lastVisit", label: "Last Visit", width: "1fr", render: (row) => row.lastVisit || "N/A" },
  ], []);

  const recentRedemptions = transformRecentRedemptionsData(recentRedemptionsData?.data?.data) || [];

  const settlementsPayload = useMemo(() => extractPayload(settlementsData) || {}, [settlementsData]);

  const invoiceRows = useMemo(() => {
    const raw = pick(settlementsPayload, "data") || [];
    if (!Array.isArray(raw)) return [];
    return raw.map((item) => ({
      id: item.id,
      restaurantName: pick(item, "restaurant.title", "restaurant.legal_business_name", "restaurant.name") || "Restaurant",
      invoiceNumber: item.invoice_number || "—",
      periodStart: pick(item, "period.start_date", "period_start"),
      periodEnd: pick(item, "period.end_date", "period_end"),
      memberSpend: normalizeAmount(pick(item, "total_member_spend", "performance_summary.eligible_member_spend", "settlement_calculation.eligible_member_spend")),
      platformFee: normalizeAmount(pick(item, "total_platform_fee", "pending_platform_fee", "settlement_calculation.platform_fee_due", "invoice_total")),
      amountDue: normalizeAmount(pick(item, "amount_due", "invoice_total")),
      status: item.invoice_status || item.status,
    }));
  }, [settlementsPayload]);

  useEffect(() => {
    const currentPage = settlementsPayload.current_page || 1;
    const lastPage = settlementsPayload.last_page || 1;
    setSettlementHasMore(currentPage < lastPage);
  }, [settlementsPayload]);

  useEffect(() => {
    if (settlementPage === 1) {
      setSettlementRowsCache(invoiceRows);
    } else {
      setSettlementRowsCache((prev) => {
        const seen = new Set(prev.map((row) => row.id));
        return [...prev, ...invoiceRows.filter((row) => !seen.has(row.id))];
      });
    }
  }, [invoiceRows, settlementPage]);

  // Settlement table columns
  const settlementColumns = [
    { key: "restaurantName", label: "Restaurant", width: "1.6fr", render: (row) => row.restaurantName },
    { key: "periodStart", label: "Period Start", width: "1fr", render: (row) => formatDateShort(row.periodStart) },
    { key: "periodEnd", label: "Period End", width: "1fr", render: (row) => formatDateShort(row.periodEnd) },
    { key: "memberSpend", label: "Member Spend", width: "1.1fr", align: "right", render: (row) => currency(row.memberSpend) },
    { key: "platformFee", label: "Platform Fee", width: "1.1fr", align: "right", render: (row) => currency(row.platformFee) },
    { key: "amountDue", label: "Amount Due", width: "1.1fr", align: "right", render: (row) => currency(row.amountDue) },
    { key: "status", label: "Status", width: "0.9fr", align: "center", render: (row) => <StatusBadge status={row.status} /> },
    {
      key: "actions",
      label: "",
      width: "1fr",
      align: "right",
      render: (row) => (
        <SecondaryButton
          variant="light-grey"
          onClick={() => navigate(`/restaurant/settlements/invoices/${row.id}`)}
          icon={arrowRightIcon}
        >
          View
        </SecondaryButton>
      ),
    },
  ];



  return (
  <div className="page-content rp-page-content">
    <Container fluid >
      {!hasActiveConnectAccount && !restaurantMeLoading && (
        <div className="card-connect-banner" style={{ marginBottom: '24px' }}>
          <div className="card-connect-banner__icon-stack">
            <div className="card-connect-banner__card card-connect-banner__card--back" />
            <div className="card-connect-banner__card card-connect-banner__card--front">
              <div className="card-connect-banner__chip" />
              <div className="card-connect-banner__lines">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>

          <div className="card-connect-banner__content">
            <h2>
              Setup Your <b>Stripe Connect</b> Account
            </h2>
            <p>
              You haven't set up your Stripe Connect account yet. <br/>
              Complete the setup to enable secure payment processing and payouts for your restaurant.
            </p>
          </div>

          <CustomButton
            variant="blue"
            iconPosition="right"
            icon={arrowRightIcon}
            loading={false}
            className="card-connect-banner__connect-btn"
            onClick={handleSetupConnectAccount}
          >
            Setup Now
          </CustomButton>
        </div>
      )}
      {/* ---- Header ---- */}
      <div className="adb-header">
        <div>
          <h1 className="adb-header__title">Dashboard</h1>
          <p className="adb-header__subtitle">Monitor restaurant performance, track key metrics, and manage settlements.</p>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Dropdown isOpen={periodMenuOpen} toggle={() => setPeriodMenuOpen((o) => !o)}>
            <DropdownToggle tag="div">
              <button className="adb-period-btn"><IconCalendar /> {getPeriodLabel(period)} <IconChevronDown /></button>
            </DropdownToggle>
            <DropdownMenu end>
              {Object.entries(PERIOD_LABELS).map(([key, label]) => (
                <DropdownItem key={key} active={period === key} onClick={() => { setPeriod(key); setSettlementPage(1); setSettlementRowsCache([]); setSettlementHasMore(true); if (key !== "custom") setDateRange({ preset: "custom", start: "", end: "" }); }}>{label}</DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          {period === "custom" && (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => { setDateRange({ ...dateRange, start: e.target.value }); setSettlementPage(1); setSettlementRowsCache([]); setSettlementHasMore(true); }}
                style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
              />
              <span style={{ color: "#6b7280" }}>to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => { setDateRange({ ...dateRange, end: e.target.value }); setSettlementPage(1); setSettlementRowsCache([]); setSettlementHasMore(true); }}
                style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
              />
            </div>
          )}
        </div>
      </div>
      <div className="adb-stats-grid" style={{ marginBottom: 24, marginTop: 24 }}>
        <StatCard
          label="Total Member Spend"
          titleColor="#1ba97f"
          icon={<IconDollar />}
          iconBg="var(--bg-sea-green)"
          value={`$${totalMemberSpend.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          deltaPct={totalMemberSpend.deltaPct}
          secondaryLabel={getPeriodLabel(period)}
          secondaryValue={`$${totalMemberSpend.secondaryValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          periodLabel={getPeriodLabel(period)}
        />
        <StatCard
          label="Vouchers Redeemed"
          titleColor="#e23d3d"
          icon={<IconTicket />}
          iconBg="var(--bg-light-red)"
          value={vouchersRedeemed.value}
          deltaPct={vouchersRedeemed.deltaPct}
          secondaryLabel={getPeriodLabel(period)}
          secondaryValue={vouchersRedeemed.secondaryValue}
          periodLabel={getPeriodLabel(period)}
          actionLabel="View All"
          onAction={handleViewRedemptions}
        />
        <StatCard
          label="Unique Customers"
          titleColor="#7b4fd6"
          icon={<IconUsers />}
          iconBg="#f1ecfc"
          value={uniqueCustomers.value}
          deltaPct={uniqueCustomers.deltaPct}
          secondaryLabel={getPeriodLabel(period)}
          secondaryValue={uniqueCustomers.secondaryValue}
          periodLabel={getPeriodLabel(period)}
          actionLabel="View All"
          onAction={handleViewCustomers}
        />
        <StatCard
          label="Avg. Order Value"
          titleColor="#f59e0b"
          icon={<IconChart />}
          iconBg="#fef3c7"
          value={totalMemberSpend.value > 0 && uniqueCustomers.value > 0 ? `$${(totalMemberSpend.value / uniqueCustomers.value).toFixed(2)}` : '$0.00'}
          deltaPct={null}
          secondaryLabel={getPeriodLabel(period)}
          secondaryValue={uniqueCustomers.value > 0 ? `${(totalMemberSpend.value / uniqueCustomers.value).toFixed(2)}` : '0.00'}
          periodLabel={getPeriodLabel(period)}
        />
      </div>

      <Row className="g-3" style={{ marginBottom: 24 }}>
        <Col xl={12}>
          <CustomerSpendBreakdownCard
            description={spendBreakdown.description}
            returning={spendBreakdown.returning}
            newCustomers={spendBreakdown.newCustomers}
          />
        </Col>
      </Row>

      <Row className="g-3 align-items-stretch" style={{ marginBottom: 24 }}>
        <Col xl={6}>
          <RecentRedemptionsCard redemptions={recentRedemptions} onViewAll={handleViewAllRedemptions} compact={true}  />
        </Col>
        <Col xl={6}>
          <SettlementCard
            periodLabel={settlement.periodLabel}
            lineItems={settlement.lineItems}
            net={settlement.net}
            note={settlement.note}
            chartSegments={settlement.chartSegments}
            totalMemberSpend={settlement.totalMemberSpend}
            estimatedNetRevenue={settlement.estimatedNetRevenue}
            status={settlement.status}
          />
        </Col>
      </Row>

      {/* ---- Restaurant Settlements ---- */}
      <div className="rp-card stl-card stl-table-card" style={{ marginBottom: 24 }}>
        <div className="rp-card__header stl-table-header">
          <div>
            <h2 className="rp-card__title">Restaurant Settlements</h2>
            <p className="rp-card__subtitle">Detailed breakdown of settlement transactions.</p>
          </div>
        </div>
        <div className="ar-table-scroll" style={{ overflowX: "auto" }}>
          <DataTable
            columns={settlementColumns}
            rows={settlementRowsCache}
            getRowKey={(r) => r.id}
            loading={settlementsLoading && settlementPage === 1}
            loadingMore={settlementsFetching && settlementPage > 1}
            onLoadMore={handleLoadMoreSettlements}
            hasMore={settlementHasMore}
            compact={true}
          />
        </div>
      </div>

      <div className="rp-card" style={{ marginBottom: 24 }}>
        <div className="rp-card__header">
          <div>
            <h2 className="rp-card__title">Top Customers</h2>
            <p className="rp-card__subtitle">Highest spending members at your location.</p>
          </div>
          <SecondaryButton
            type="button"
            variant="orange"
            onClick={handleViewAllCustomers}
          >
            View All
          </SecondaryButton>
        </div>
        <DataTable
          columns={topCustomerColumns}
          rows={topCustomers}
          getRowKey={(row) => row.id}
          loading={topCustomersLoading}
          compact
        />
      </div>
    </Container>
  </div>
  );
};

RestaurantDashboardPage.propTypes = {
  onViewRedemptions: PropTypes.func,
  onViewCustomers: PropTypes.func,
  onViewAllRedemptions: PropTypes.func,
  onViewAllCustomers: PropTypes.func,
};

RestaurantDashboardPage.defaultProps = {
  onViewRedemptions: () => {},
  onViewCustomers: () => {},
  onViewAllRedemptions: () => {},
  onViewAllCustomers: () => {},
};

export default RestaurantDashboardPage;
